import { useEffect, useMemo, useState, type ReactNode } from "react";
import { StreamContext, type StreamState } from "./eventStreamContext";
import { getPageActivity, subscribePageActivity, usePageActivity } from "./pageActivity";

const WATCHDOG_MS = 25_000;
const RETRY_DELAYS_MS = [2_000, 4_000, 8_000, 16_000, 30_000] as const;

function snapshotUpdatedAt(data: unknown): string | null {
  try {
    const parsed: unknown = typeof data === "string" ? JSON.parse(data) : null;
    const updatedAt = (parsed as { updatedAt?: unknown } | null)?.updatedAt;
    return typeof updatedAt === "string" && !Number.isNaN(Date.parse(updatedAt)) ? updatedAt : null;
  } catch {
    return null;
  }
}

export function EventStreamProvider({ children }: { children: ReactNode }) {
  const activity = usePageActivity();
  const [state, setState] = useState<StreamState>(() =>
    getPageActivity().active ? "connecting" : "disconnected",
  );
  const [lastEventAt, setLastEventAt] = useState<string | null>(null);
  const [eventActivation, setEventActivation] = useState<number | null>(null);

  useEffect(() => {
    let live = true;
    let retryTimer = 0;
    let watchdogTimer = 0;
    let failures = 0;
    let current: { source: EventSource; activation: number; reconnecting: boolean } | null = null;

    const retire = () => {
      window.clearTimeout(retryTimer);
      window.clearTimeout(watchdogTimer);
      retryTimer = 0;
      watchdogTimer = 0;
      const retired = current;
      current = null;
      retired?.source.close();
    };
    const valid = (candidate: NonNullable<typeof current>) =>
      live && current === candidate && getPageActivity().active;
    const schedule = (presentation: StreamState) => {
      retire();
      failures += 1;
      setState(presentation);
      const delay = RETRY_DELAYS_MS[Math.min(failures - 1, RETRY_DELAYS_MS.length - 1)];
      retryTimer = window.setTimeout(connect, delay + Math.random() * Math.min(1_000, delay / 4));
    };
    const touch = (candidate: NonNullable<typeof current>, eventName: string) => {
      if (!valid(candidate) || candidate.activation !== getPageActivity().activation) return;
      failures = 0;
      window.clearTimeout(watchdogTimer);
      watchdogTimer = window.setTimeout(() => {
        if (valid(candidate)) schedule("stale");
      }, WATCHDOG_MS);
      setLastEventAt(new Date().toISOString());
      setEventActivation(candidate.activation);
      setState("connected");
      // A positions snapshot moves only the positions page, never every data reader.
      if (eventName !== "heartbeat" && eventName !== "positions") {
        window.dispatchEvent(new CustomEvent("auto-pools:server-event", { detail: eventName }));
      }
    };
    function connect(resuming = false) {
      const page = getPageActivity();
      if (!live || !page.active || current) return;
      setState(failures || resuming ? "reconnecting" : "connecting");
      const candidate = {
        source: new EventSource("/api/events"),
        activation: page.activation,
        reconnecting: failures > 0 || resuming,
      };
      current = candidate;
      watchdogTimer = window.setTimeout(() => {
        if (valid(candidate)) schedule("stale");
      }, WATCHDOG_MS);
      candidate.source.onopen = () => {
        if (valid(candidate)) setState(candidate.reconnecting ? "reconnecting" : "connecting");
      };
      candidate.source.onmessage = () => {
        touch(candidate, "message");
      };
      for (const name of [
        "status",
        "positions",
        "operations",
        "autopilot",
        "spot",
        "heartbeat",
        "revoked",
      ]) {
        candidate.source.addEventListener(name, (event) => {
          if (!valid(candidate)) return;
          touch(candidate, name);
          if (name === "revoked") window.dispatchEvent(new Event("auto-pools:unauthorized"));
          if (name === "positions") {
            const updatedAt = snapshotUpdatedAt((event as MessageEvent<unknown>).data);
            if (updatedAt)
              window.dispatchEvent(
                new CustomEvent("auto-pools:positions-snapshot", { detail: { updatedAt } }),
              );
          }
        });
      }
      candidate.source.onerror = () => {
        if (valid(candidate)) schedule("reconnecting");
      };
    }
    const onActivity = () => {
      if (!getPageActivity().active) {
        retire();
        setState("disconnected");
        return;
      }
      retire();
      setState("reconnecting");
      connect(true);
    };
    const unsubscribe = subscribePageActivity(onActivity);
    connect();
    return () => {
      live = false;
      unsubscribe();
      retire();
    };
  }, []);

  const value = useMemo(
    () => ({
      state,
      lastEventAt,
      active: activity.active,
      activation: activity.activation,
      eventActivation,
    }),
    [activity, eventActivation, lastEventAt, state],
  );
  return <StreamContext.Provider value={value}>{children}</StreamContext.Provider>;
}
